/**
 * InspectionResultBadge Component
 *
 * Displays inspection result with color-coded badge.
 * Shows pass (green), fail (red), or pending (orange) status.
 *
 * @module components/InspectionResultBadge
 */

import { View, ViewStyle, TextStyle } from "react-native"
import Icon from "react-native-vector-icons/MaterialIcons"

import { colors } from "@/theme"

import { Text } from "./Text"

export interface InspectionResultBadgeProps {
  /**
   * Inspection pass/fail status
   * - true: Passed (green)
   * - false: Failed (red)
   * - null/undefined: Pending (orange)
   */
  passed?: boolean | null

  /**
   * Detailed result code
   * Pass codes: OM, GM, HW
   * Fail codes: EM, VM, VU
   */
  result?: string

  /**
   * Optional style override
   */
  style?: ViewStyle
}

/**
 * InspectionResultBadge Component
 *
 * @example
 * ```tsx
 * <InspectionResultBadge passed={true} result="OM" />
 * <InspectionResultBadge passed={false} result="EM" />
 * <InspectionResultBadge passed={null} />
 * ```
 */
export function InspectionResultBadge({
  passed,
  result,
  style: $styleOverride,
}: InspectionResultBadgeProps) {
  // Pending state
  if (passed === null || passed === undefined) {
    return (
      <View style={[$badgeContainer, $badgePending, $styleOverride]}>
        <View style={$iconContainer}>
          <Icon name="schedule" size={14} color={colors.warning} />
        </View>
        <Text style={$textPending}>Prüfergebnis ausstehend</Text>
      </View>
    )
  }

  // Pass state
  if (passed) {
    return (
      <View style={[$badgeContainer, $badgePass, $styleOverride]}>
        <View style={$iconContainer}>
          <Icon name="check-circle" size={14} color={colors.success} />
        </View>
        <Text style={$textPass}>Bestanden {result ? `(${result})` : ""}</Text>
      </View>
    )
  }

  // Fail state
  return (
    <View style={[$badgeContainer, $badgeFail, $styleOverride]}>
      <View style={$iconContainer}>
        <Icon name="cancel" size={14} color={colors.error} />
      </View>
      <Text style={$textFail}>Nicht bestanden {result ? `(${result})` : ""}</Text>
    </View>
  )
}

// Base Styles - COMPACT VERSION
const $badgeContainer: ViewStyle = {
  flexDirection: "row",
  alignItems: "center",
  paddingHorizontal: 10, // Reduced from 14
  paddingVertical: 6, // Reduced from 10
  borderRadius: 8, // Reduced from 10
  borderWidth: 1,
  alignSelf: "flex-start",
  // Subtle shadow for depth
  shadowColor: "rgba(0, 0, 0, 0.08)",
  shadowOffset: { width: 0, height: 1 }, // Reduced from 2
  shadowOpacity: 1,
  shadowRadius: 3, // Reduced from 4
  elevation: 1, // Reduced from 2
}

const $iconContainer: ViewStyle = {
  marginRight: 6, // Reduced from 8
  // Icon background container for better visual hierarchy
  backgroundColor: "rgba(255, 255, 255, 0.5)",
  borderRadius: 10, // Reduced from 12
  padding: 2,
}

// Pending Styles (Orange)
const $badgePending: ViewStyle = {
  backgroundColor: colors.badgePendingBg, // Softer light orange (was #FFF3E0)
  borderColor: "rgba(255, 152, 0, 0.2)", // Subtle border (was #FFCC80)
  // Enhanced shadow with orange tint
  shadowColor: "rgba(255, 152, 0, 0.15)",
}

const $textPending: TextStyle = {
  fontSize: 12, // Reduced from 14
  color: colors.badgePendingText, // Darker orange for better contrast
  fontWeight: "600", // Semibold (was 500)
  letterSpacing: 0, // Changed from 0.1 to fix React Native Web text node error
}

// Pass Styles (Green)
const $badgePass: ViewStyle = {
  backgroundColor: colors.badgePassedBg, // Softer light green (was #E8F5E9)
  borderColor: "rgba(76, 175, 80, 0.2)", // Subtle border (was #A5D6A7)
  // Enhanced shadow with green tint
  shadowColor: "rgba(76, 175, 80, 0.15)",
}

const $textPass: TextStyle = {
  fontSize: 12, // Reduced from 14
  color: colors.badgePassedText, // Darker green for better contrast
  fontWeight: "600", // Semibold (was 500)
  letterSpacing: 0, // Changed from 0.1 to fix React Native Web text node error
}

// Fail Styles (Red)
const $badgeFail: ViewStyle = {
  backgroundColor: colors.badgeFailedBg, // Softer light red (was #FFEBEE)
  borderColor: "rgba(244, 67, 54, 0.2)", // Subtle border (was #EF9A9A)
  // Enhanced shadow with red tint
  shadowColor: "rgba(244, 67, 54, 0.15)",
}

const $textFail: TextStyle = {
  fontSize: 12, // Reduced from 14
  color: colors.badgeFailedText, // Darker red for better contrast
  fontWeight: "600", // Semibold (was 500)
  letterSpacing: 0, // Changed from 0.1 to fix React Native Web text node error
}
